// Admin Recall management — list + create modal
import React, { useEffect, useState } from "react";
import { AlertTriangle, Plus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api } from "../../api/client";

const STATUS_TONE = {
    active: "bg-rose-100 text-rose-700",
    resolved: "bg-zinc-200 text-zinc-700",
};
const SEV_TONE = {
    low: "bg-yellow-100 text-yellow-800",
    medium: "bg-amber-100 text-amber-800",
    high: "bg-orange-100 text-orange-800",
    critical: "bg-rose-200 text-rose-900",
};

const EMPTY_DRAFT = { product_id: "", batch_ids: [], reason: "", severity: "medium", public_notice: "" };

const AdminRecalls = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState("all");
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [productSearch, setProductSearch] = useState("");
    const [productResults, setProductResults] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [batches, setBatches] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        const params = {};
        if (status !== "all") params.status = status;
        api.get("/admin/recalls", { params }).then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [status]);

    const search = async (e) => {
        e.preventDefault();
        if (!productSearch.trim()) return;
        const { data } = await api.get("/products", { params: { q: productSearch.trim(), page_size: 8 } });
        setProductResults(data.items || []);
    };

    const pickProduct = async (p) => {
        setSelectedProduct(p);
        setDraft({ ...draft, product_id: p.id, batch_ids: [] });
        // Load batches for this product
        try {
            const { data } = await api.get("/admin/inventory/batches-v2", { params: { product_id: p.id, page_size: 50 } });
            setBatches(data.items || []);
        } catch {
            setBatches([]);
        }
    };

    const toggleBatch = (bid) => {
        setDraft((d) => ({
            ...d,
            batch_ids: d.batch_ids.includes(bid) ? d.batch_ids.filter((x) => x !== bid) : [...d.batch_ids, bid],
        }));
    };

    const submit = async () => {
        if (!draft.product_id) { toast.error("Select a product"); return; }
        if (draft.batch_ids.length === 0) { toast.error("Select at least one batch"); return; }
        if (!draft.reason || draft.reason.length < 5) { toast.error("Reason ≥5 chars"); return; }
        if (!draft.public_notice || draft.public_notice.length < 10) { toast.error("Public notice ≥10 chars"); return; }
        setSaving(true);
        try {
            const { data } = await api.post("/admin/recalls", draft);
            toast.success(`Recall ${data.recall_no} created · ${data.notified_customers} customer(s) notified`);
            setOpen(false);
            setDraft(EMPTY_DRAFT);
            setSelectedProduct(null);
            setBatches([]);
            setProductResults([]);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Failed");
        } finally {
            setSaving(false);
        }
    };

    const resolve = async (id) => {
        if (!window.confirm("Mark this recall as resolved?")) return;
        try {
            await api.post(`/admin/recalls/${id}/resolve`);
            toast.success("Recall resolved");
            load();
        } catch { toast.error("Failed"); }
    };

    return (
        <AdminAuthGuard>
            <AdminLayout title="Product recalls" subtitle="Issue and manage product recall notices">
                <div className="mb-5 flex flex-wrap items-center gap-2">
                    {["all", "active", "resolved"].map((s) => (
                        <button key={s} onClick={() => setStatus(s)} className={`rounded-full px-3 py-1.5 text-xs font-medium ${status === s ? "bg-brand-700 text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"}`} data-testid={`admin-recalls-tab-${s}`}>{s}</button>
                    ))}
                    <button onClick={() => setOpen(true)} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700" data-testid="admin-recall-new">
                        <Plus className="h-4 w-4" /> Issue recall
                    </button>
                </div>

                {loading ? <div className="h-48 skeleton rounded-2xl" /> : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-zinc-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="admin-recalls-empty">
                        <AlertTriangle className="mx-auto h-8 w-8 text-slate-300" />
                        <p className="mt-2">No recalls in this view.</p>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="admin-recalls-list">
                        {items.map((r) => (
                            <article key={r.id} className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-sm" data-testid={`admin-recall-row-${r.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-xs font-semibold">{r.recall_no}</span>
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[r.status]}`}>{r.status}</span>
                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${SEV_TONE[r.severity]}`}>{r.severity}</span>
                                        </div>
                                        <h3 className="mt-1 text-base font-semibold">{r.product_name}</h3>
                                        <p className="mt-1 text-sm text-slate-700">{r.reason}</p>
                                        <p className="mt-2 text-[11px] text-slate-500">{r.affected_batch_ids?.length || 0} batches · initiated {new Date(r.initiated_at).toLocaleDateString()}</p>
                                    </div>
                                    {r.status === "active" && (
                                        <button onClick={() => resolve(r.id)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs font-medium hover:bg-zinc-50" data-testid={`admin-recall-resolve-${r.id}`}>
                                            Mark resolved
                                        </button>
                                    )}
                                </div>
                                <details className="mt-3 text-sm">
                                    <summary className="cursor-pointer text-xs font-medium text-brand-700 hover:underline">View public notice</summary>
                                    <p className="mt-2 rounded-md bg-zinc-50 p-3 text-xs whitespace-pre-wrap">{r.public_notice}</p>
                                </details>
                            </article>
                        ))}
                    </div>
                )}

                {open && (
                    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
                        <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-xl" data-testid="admin-recall-modal">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="label-uppercase">Product recall</p>
                                    <h2 className="text-lg font-semibold text-rose-700">Issue a recall</h2>
                                </div>
                                <button onClick={() => setOpen(false)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-4 w-4" /></button>
                            </div>

                            <div className="mt-4 space-y-4 text-sm">
                                <div>
                                    <p className="label-uppercase mb-2">1. Find affected product</p>
                                    <form onSubmit={search} className="flex gap-2">
                                        <input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search product name…" className="flex-1 rounded-md border border-zinc-200 px-3 py-1.5" data-testid="admin-recall-product-search" />
                                        <button type="submit" className="rounded-md border border-zinc-200 px-3 py-1.5 text-xs">Search</button>
                                    </form>
                                    {productResults.length > 0 && !selectedProduct && (
                                        <ul className="mt-2 max-h-32 overflow-y-auto rounded-md border border-zinc-100 text-xs">
                                            {productResults.map((p) => (
                                                <li key={p.id} className="flex items-center justify-between border-b border-zinc-50 px-2 py-1.5 last:border-b-0">
                                                    <span>{p.name} · {p.brand}</span>
                                                    <button onClick={() => pickProduct(p)} className="rounded-md bg-brand-700 px-2 py-0.5 text-[10px] text-white" data-testid={`admin-recall-pick-${p.id}`}>Pick</button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {selectedProduct && (
                                        <div className="mt-2 flex items-center justify-between rounded-md bg-brand-50 p-2 text-xs">
                                            <span><strong>{selectedProduct.name}</strong> · {selectedProduct.brand}</span>
                                            <button onClick={() => { setSelectedProduct(null); setBatches([]); setDraft({ ...draft, product_id: "", batch_ids: [] }); }} className="text-zinc-500 hover:text-rose-600">Change</button>
                                        </div>
                                    )}
                                </div>

                                {selectedProduct && (
                                    <div>
                                        <p className="label-uppercase mb-2">2. Select affected batches ({draft.batch_ids.length} selected)</p>
                                        {batches.length === 0 ? (
                                            <p className="text-xs text-zinc-500">No batches found for this product.</p>
                                        ) : (
                                            <div className="max-h-40 overflow-y-auto rounded-md border border-zinc-100" data-testid="admin-recall-batches">
                                                {batches.map((b) => (
                                                    <label key={b.id} className={`flex cursor-pointer items-center gap-2 border-b border-zinc-50 px-2 py-1.5 text-xs last:border-b-0 hover:bg-zinc-50 ${draft.batch_ids.includes(b.id) ? "bg-rose-50" : ""}`} data-testid={`admin-recall-batch-${b.id}`}>
                                                        <input type="checkbox" checked={draft.batch_ids.includes(b.id)} onChange={() => toggleBatch(b.id)} />
                                                        <span>Batch <strong>{b.batch_no || b.id.slice(0, 8)}</strong> · {b.location_code || "—"} · qty {b.qty_remaining ?? b.qty_received} · exp {b.expiry_date || "—"} · {b.batch_status || "available"}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <p className="label-uppercase">3. Reason (internal)</p>
                                    <input value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} placeholder="e.g. Contamination detected in batch B/12345" className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-1.5" data-testid="admin-recall-reason" />
                                </div>

                                <div>
                                    <p className="label-uppercase">4. Severity</p>
                                    <select value={draft.severity} onChange={(e) => setDraft({ ...draft, severity: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-1.5" data-testid="admin-recall-severity">
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="critical">Critical</option>
                                    </select>
                                </div>

                                <div>
                                    <p className="label-uppercase">5. Public notice (shown to customers)</p>
                                    <textarea rows={4} value={draft.public_notice} onChange={(e) => setDraft({ ...draft, public_notice: e.target.value })} placeholder="We are recalling Batch B/12345 of XYZ due to…" className="mt-1 w-full rounded-md border border-zinc-200 p-2 text-sm" data-testid="admin-recall-notice" />
                                </div>
                            </div>

                            <div className="mt-5 flex justify-end gap-2">
                                <button onClick={() => setOpen(false)} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm">Cancel</button>
                                <button onClick={submit} disabled={saving} className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50" data-testid="admin-recall-submit">
                                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <AlertTriangle className="h-3 w-3" />}
                                    {saving ? "Creating…" : "Issue recall"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </AdminLayout>
        </AdminAuthGuard>
    );
};

export default AdminRecalls;
